import { apiErrorMessage, customFetch } from '@agos/api-client'
import { getGetDeliveryZonesQueryKey, useGetDeliveryZones } from '@agos/api-client/orders'
import {
  T,
  ZONE_COLORS,
  deliveryFeeCents,
  formatCents,
  tt,
  type DeliveryZone,
  type LatLng,
} from '@agos/shared'
import { PageHeader, Spinner, StatusBadge, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  Circle as CircleIcon,
  Loader2,
  MapPin,
  Pencil,
  Pentagon,
  Plus,
  Power,
  RotateCcw,
  Save,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { ZoneMap, type DrawMode } from './ZoneMap'
import {
  addTier,
  describeZone,
  isZoneDrawn,
  newZone,
  removeTier,
  updateTier,
} from './zone-draft'

/**
 * `/delivery-zones` — where the franchise delivers and what it charges.
 *
 * The arithmetic behind it — `findZoneForPoint`, `deliveryFeeCents` — is in
 * `@agos/shared` with 16 tests, because the order builder and the dispatch board
 * read the same answers. This page is the editor around it.
 *
 * **`PUT /delivery-zones` replaces the whole collection.** So the page holds one
 * local array and writes it whole; a save assembled from partial state would
 * delete whatever it left out. I proved that while porting: an empty PUT as an
 * existence probe removed all 31 zones from the local database.
 *
 * Ported from legacy `pages/delivery-zones.tsx`.
 */

const SAVED_FLASH_MS = 2500

/** Ids are only meaningful to this page until the array is saved. */
const makeId = () => crypto.randomUUID()

export function DeliveryZonesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetDeliveryZones()

  const [draft, setDraft] = useState<DeliveryZone[] | null>(null)
  const [loadedFrom, setLoadedFrom] = useState<unknown>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [drawMode, setDrawMode] = useState<DrawMode>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [mobilePane, setMobilePane] = useState<'zones' | 'map'>('zones')

  // Adopt the server's array once, and again whenever a save refetches it. The
  // draft is the user's between those points.
  //
  // Through `unknown`, and the reason is worth writing down. `delivery_zones.polygon` and `.tiers` are
  // free-form `jsonb`, so the generated model types them as free-form objects — which is what they
  // are. `DeliveryZone` here says `LatLng[]` and `PriceTier[]`, which is what this screen and
  // `isPointInPolygon` require. The two do not overlap structurally and a direct cast is a claim
  // TypeScript is right to refuse.
  //
  // It compiled before only because springdoc described those fields as Jackson's `JsonNode` — an
  // interface of predicates that overlaps with anything — so the assertion was always this same
  // assumption, just invisible. It is stated rather than hidden now. The shape is guaranteed by the
  // only writer, this page's own save.
  if (data !== loadedFrom) {
    setLoadedFrom(data)
    setDraft(Array.isArray(data) ? (data as unknown as DeliveryZone[]) : [])
  }

  // Memoised so the undrawn-zone check below does not recompute on every
  // render just because `?? []` makes a new array.
  const zones = useMemo(() => draft ?? [], [draft])
  const editing = zones.find((zone) => zone.id === editingId) ?? null

  const updateZone = (id: string, changes: Partial<DeliveryZone>) =>
    setDraft((current) =>
      (current ?? []).map((zone) => (zone.id === id ? { ...zone, ...changes } : zone)),
    )

  const undrawn = useMemo(() => zones.filter((zone) => !isZoneDrawn(zone)), [zones])

  const save = async () => {
    setIsSaving(true)
    try {
      // The whole array, every time — that is what the endpoint means.
      await customFetch('/api/v1/delivery-zones', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(zones),
      })
      await queryClient.invalidateQueries({ queryKey: getGetDeliveryZonesQueryKey() })
      setJustSaved(true)
      setTimeout(() => setJustSaved(false), SAVED_FLASH_MS)
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Oblasti sa nepodarilo uložiť.')),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const addZone = () => {
    const zone = newZone(zones.length, makeId)
    setDraft((current) => [...(current ?? []), zone])
    setEditingId(zone.id)
    setDrawMode(null)
  }

  const deleteZone = (id: string) => {
    setDraft((current) => (current ?? []).filter((zone) => zone.id !== id))
    if (editingId === id) setEditingId(null)
  }

  /** A map click is either a circle's centre or a polygon's next corner. */
  const placePoint = (point: LatLng) => {
    if (!editing) return
    if (drawMode === 'circle') {
      updateZone(editing.id, { center: point })
      setDrawMode(null)
      return
    }
    if (drawMode === 'polygon') {
      updateZone(editing.id, { polygon: [...(editing.polygon ?? []), point] })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row gap-0 h-[calc(100vh-130px)] min-h-[600px] -mx-3 sm:-mx-6 -mt-2">
      <div className="md:hidden flex border-b border-border bg-card">
        {(['zones', 'map'] as const).map((pane) => (
          <button
            key={pane}
            type="button"
            onClick={() => setMobilePane(pane)}
            className={cn(
              'flex-1 py-2.5 text-sm font-bold transition-colors',
              mobilePane === pane
                ? 'text-primary border-b-2 border-primary -mb-px'
                : 'text-muted-foreground',
            )}
          >
            {pane === 'zones' ? tt('Zóny') : tt('Mapa')}
          </button>
        ))}
      </div>

      <div
        className={cn(
          'w-full md:w-[380px] shrink-0 flex-col border-r border-border bg-card overflow-y-auto',
          mobilePane === 'zones' ? 'flex' : 'hidden md:flex',
        )}
      >
        <div className="border-b border-border sticky top-0 bg-card z-10 px-5 py-4">
          <PageHeader
            title={tt('Doručovacie zóny')}
            icon={<MapPin className="w-5 h-5" />}
            className="mb-0"
            actions={
              <button
                type="button"
                onClick={() => void save()}
                disabled={isSaving}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50',
                  justSaved
                    ? 'bg-emerald-500 text-white'
                    : 'bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50',
                )}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> <T>Ukladám...</T>
                  </>
                ) : justSaved ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> <T>Uložené</T>
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" /> <T>Uložiť</T>
                  </>
                )}
              </button>
            }
          />
          {undrawn.length > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              {/* A zone `findZoneForPoint` skips never matches an address, so it
                  quotes undeliverable instead of the fee it was drawn for. */}
              <T>
                Niektoré zóny nie sú nakreslené - adresa v nich sa vyhodnotí ako mimo rozvozu.
              </T>
            </p>
          )}
        </div>

        <div className="flex-1 divide-y divide-border">
          {zones.length === 0 && (
            <div className="px-5 py-10 text-center text-muted-foreground text-sm">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <T>Zatiaľ žiadne zóny.</T>
            </div>
          )}

          {zones.map((zone) => (
            <div
              key={zone.id}
              className={cn(
                'transition-colors',
                editingId === zone.id ? 'bg-primary/5' : 'hover:bg-muted/40',
              )}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <span
                  className="w-3 h-3 rounded-full shrink-0 ring-2 ring-white"
                  style={{ background: zone.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{zone.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {zone.type === 'circle' ? tt('Kruh') : tt('Polygón')} · {describeZone(zone)}
                    {zone.type === 'polygon' && ` ${tt('bodov')}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => updateZone(zone.id, { enabled: !zone.enabled })}
                  title={zone.enabled ? tt('Vypnúť') : tt('Zapnúť')}
                  className="transition-all hover:opacity-80"
                >
                  <StatusBadge
                    label={zone.enabled ? tt('Aktívna') : tt('Vypnutá')}
                    color={zone.enabled ? 'emerald' : 'zinc'}
                    icon={Power}
                  />
                </button>
                <button
                  type="button"
                  aria-label={`${tt('Upraviť')} ${zone.name}`}
                  onClick={() => setEditingId(editingId === zone.id ? null : zone.id)}
                  className={cn(
                    'p-1.5 rounded-lg transition-all',
                    editingId === zone.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`${tt('Zmazať')} ${zone.name}`}
                  onClick={() => deleteZone(zone.id)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {editingId === zone.id && (
                <ZoneEditor
                  zone={zone}
                  drawMode={drawMode}
                  onChange={(changes) => updateZone(zone.id, changes)}
                  onReplace={(next) => setDraft((current) => (current ?? []).map((entry) => (entry.id === zone.id ? next : entry)))}
                  onDrawMode={setDrawMode}
                />
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border sticky bottom-0 bg-card">
          <button
            type="button"
            onClick={addZone}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> <T>Pridať zónu</T>
          </button>
        </div>
      </div>

      <div className={cn('flex-1', mobilePane === 'map' ? 'block' : 'hidden md:block')}>
        <ZoneMap zones={zones} editingId={editingId} drawMode={drawMode} onPoint={placePoint} />
      </div>
    </div>
  )
}

const FIELD_CLASSES =
  'px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

function ZoneEditor({
  zone,
  drawMode,
  onChange,
  onReplace,
  onDrawMode,
}: {
  zone: DeliveryZone
  drawMode: DrawMode
  onChange: (changes: Partial<DeliveryZone>) => void
  onReplace: (zone: DeliveryZone) => void
  onDrawMode: (mode: DrawMode) => void
}) {
  return (
    <div className="px-4 pb-4 space-y-4 border-t border-border/50 pt-3 bg-background/50">
      <div className="flex gap-2">
        <input
          value={zone.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder={tt('Názov oblasti')}
          aria-label={tt('Názov oblasti')}
          className={cn(FIELD_CLASSES, 'flex-1')}
        />
        <div className="flex gap-1">
          {ZONE_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={color}
              onClick={() => onChange({ color })}
              className={cn(
                'w-6 h-6 rounded-full transition-all',
                zone.color === color
                  ? 'ring-2 ring-offset-1 ring-foreground scale-110'
                  : 'opacity-60 hover:opacity-100',
              )}
              style={{ background: color }}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(['circle', 'polygon'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => {
              onChange({ type })
              onDrawMode(null)
            }}
            className={cn(
              'flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold border transition-all',
              zone.type === type
                ? 'bg-primary/10 border-primary/40 text-primary'
                : 'border-border text-muted-foreground hover:border-muted-foreground',
            )}
          >
            {type === 'circle' ? (
              <>
                <CircleIcon className="w-3.5 h-3.5" /> {tt('Kruh')}
              </>
            ) : (
              <>
                <Pentagon className="w-3.5 h-3.5" /> {tt('Polygón')}
              </>
            )}
          </button>
        ))}
      </div>

      {zone.type === 'circle' ? (
        <div className="space-y-2">
          <label className="block space-y-1">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              <T>Polomer (m)</T>
            </span>
            <input
              type="number"
              min="50"
              step="50"
              value={zone.radius ?? 0}
              onChange={(event) => onChange({ radius: Number(event.target.value) || 0 })}
              aria-label={tt('Polomer (m)')}
              className={cn(FIELD_CLASSES, 'w-full tabular-nums')}
            />
          </label>
          <button
            type="button"
            onClick={() => onDrawMode(drawMode === 'circle' ? null : 'circle')}
            className={cn(
              'w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border transition-all',
              drawMode === 'circle'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-muted-foreground',
            )}
          >
            <MapPin className="w-3.5 h-3.5" />
            {drawMode === 'circle' ? tt('Kliknite na mapu') : tt('Nastaviť stred na mape')}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => onDrawMode(drawMode === 'polygon' ? null : 'polygon')}
            className={cn(
              'w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border transition-all',
              drawMode === 'polygon'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-muted-foreground',
            )}
          >
            <Pentagon className="w-3.5 h-3.5" />
            {drawMode === 'polygon'
              ? `${tt('Klikajte na mapu')} (${zone.polygon?.length ?? 0})`
              : tt('Kresliť na mape')}
          </button>
          {(zone.polygon?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => onChange({ polygon: [] })}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border border-border text-muted-foreground hover:text-red-500 transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" /> <T>Vymazať body</T>
            </button>
          )}
        </div>
      )}

      <label className="block space-y-1">
        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          <T>Minimálna objednávka (€)</T>
        </span>
        <input
          type="number"
          min="0"
          step="0.5"
          value={String(zone.minOrder ?? 0)}
          onChange={(event) => onChange({ minOrder: Number(event.target.value) || 0 })}
          aria-label={tt('Minimálna objednávka (€)')}
          className={cn(FIELD_CLASSES, 'w-full tabular-nums')}
        />
      </label>

      <div className="space-y-2">
        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          <T>Cenové pásma</T>
        </p>
        {zone.tiers.map((tier) => {
          // Live check against the shared arithmetic: a band whose upper bound is
          // at or below the minimum can never be reached (10 of the 31 real zones
          // have one).
          const unreachable =
            tier.orderTo !== null &&
            tier.orderTo !== undefined &&
            Number(tier.orderTo) <= Number(zone.minOrder ?? 0)

          return (
            <div key={tier.id} className="flex items-center gap-1.5">
              <input
                type="number"
                min="0"
                step="0.5"
                value={String(tier.orderFrom ?? 0)}
                onChange={(event) =>
                  onReplace(updateTier(zone, tier.id, { orderFrom: Number(event.target.value) || 0 }))
                }
                aria-label={tt('Od (€)')}
                className={cn(FIELD_CLASSES, 'w-20 tabular-nums')}
              />
              <span className="text-muted-foreground text-xs">–</span>
              <input
                type="number"
                min="0"
                step="0.5"
                value={tier.orderTo === null || tier.orderTo === undefined ? '' : String(tier.orderTo)}
                placeholder="∞"
                onChange={(event) =>
                  onReplace(
                    updateTier(zone, tier.id, {
                      orderTo: event.target.value === '' ? null : Number(event.target.value),
                    }),
                  )
                }
                aria-label={tt('Do (€)')}
                className={cn(
                  FIELD_CLASSES,
                  'w-20 tabular-nums',
                  unreachable && 'border-amber-500/60',
                )}
                title={unreachable ? tt('Toto pásmo je nedosiahnuteľné - je pod minimálnou objednávkou.') : undefined}
              />
              <span className="text-muted-foreground text-xs">→</span>
              <input
                type="number"
                min="0"
                step="0.1"
                value={String(tier.deliveryFee ?? 0)}
                onChange={(event) =>
                  onReplace(
                    updateTier(zone, tier.id, { deliveryFee: Number(event.target.value) || 0 }),
                  )
                }
                aria-label={tt('Poplatok (€)')}
                className={cn(FIELD_CLASSES, 'w-20 tabular-nums')}
              />
              <button
                type="button"
                aria-label={tt('Zmazať pásmo')}
                onClick={() => onReplace(removeTier(zone, tier.id))}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 transition-all"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )
        })}
        <button
          type="button"
          onClick={() => onReplace(addTier(zone, makeId))}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <Plus className="w-3 h-3" /> <T>Pridať pásmo</T>
        </button>
        <p className="text-[11px] text-muted-foreground">
          {/* Read through the same function the order builder uses, so what the
              manager sees here is what a customer will be charged. */}
          <T>Príklad - objednávka za 25 €:</T>{' '}
          {(() => {
            const fee = deliveryFeeCents(25, zone)
            return fee === null ? tt('mimo rozvozu') : formatCents(fee)
          })()}
        </p>
      </div>
    </div>
  )
}
